import React, { useRef, useState } from "react";
import { Box, Paper, useTheme, useMediaQuery, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PageTitle from "../../components/PageTitle";
import SideModal from "../../components/SideModal";
import ReportForm from "./Components/ReportForm";
import ReportDataGrid from "./Components/ReportDataGrid/ReportDataGrid";
import ReportCustomToolbar from "./Components/ReportDataGrid/ReportCustomToolbar";
import { useDispatch } from "react-redux";
import { fetchReports } from "../../store/Reports/reportsThunks";

const Reports = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const gridApiRef = useRef(null);

  const handleOpenForm = () => setOpen(true);
  const handleCloseForm = () => {
    setOpen(false);
    dispatch(fetchReports({ page: 1, search: searchQuery }));
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    dispatch(fetchReports({ page: 1, search: query }));
  };

  const handleRefresh = () => {
    dispatch(fetchReports({ page: 1, search: searchQuery }));
  };

  const handleFilterClick = () => {
    if (gridApiRef.current) {
      gridApiRef.current.showFilterPanel();
    }
  };

  const handleColumnClick = () => {
    if (gridApiRef.current) {
      gridApiRef.current.showPreferences("columns");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        backgroundColor: (theme) => theme.palette.background.default,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          p: isSmallScreen ? 1 : 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            alignItems: isSmallScreen ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: isSmallScreen ? 2 : 0,
          }}
        >
          <PageTitle title="Reports" subtitle="View and generate reports" />
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallScreen ? "column" : "row",
              alignItems: isSmallScreen ? "stretch" : "center",
              gap: 2,
              width: isSmallScreen ? "100%" : "auto",
            }}
          >
            <ReportCustomToolbar
              onSearch={handleSearch}
              searchQuery={searchQuery}
              onRefresh={handleRefresh}
              onFilterClick={handleFilterClick}
              onColumnClick={handleColumnClick}
              isSmallScreen={isSmallScreen}
            />
            <Button
              startIcon={<AddIcon />}
              onClick={handleOpenForm}
              variant="contained"
              size="small"
              fullWidth={isSmallScreen}
              sx={{
                backgroundColor: (theme) => theme.palette.background.button,
                color: (theme) => theme.palette.common.white,
                textTransform: "none",
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.button,
                },
              }}
            >
              Generate Report
            </Button>
          </Box>
        </Box>
      </Box>

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          position: "relative",
          height: "calc(100vh - 120px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "auto",
            "& .MuiDataGrid-root": {
              border: "none",
              backgroundColor: (theme) => theme.palette.background.paper,
              padding: 0,
              margin: 0,
            },
          }}
        >
          <ReportDataGrid apiRef={gridApiRef} />
        </Paper>
      </Box>

      {/* Modal */}
      <SideModal open={open} onClose={handleCloseForm} title="Generate Report">
        <ReportForm onSuccess={handleCloseForm} />
      </SideModal>
    </Box>
  );
};

export default Reports;
